import type { SupabaseClient } from '@supabase/supabase-js';

import { resolveEventDisplayName } from '../../../utils/eventDisplayName';
import { getEventStartMs } from './eventStart';

/** Group option for proximity group picker (M4); no active-group persistence here. */
export type UserGroupOption = {
  id: string;
  groupName: string;
  /** The group's event — what the festival map (and anything else event-scoped) keys on. */
  eventId: string | null;
  eventName: string | null;
  eventDate: string | null;
  /**
   * When the event starts (ISO): event_datetime, else event_date + event_time in the event's zone (getEventStartMs).
   * Optional: a group list persisted before 2026-10-16 lacks it, and readers fall back to eventDate.
   */
  eventStartsAt?: string | null;
  /** When the event is over (events.ends_at, migration 134); null when unknown. */
  eventEndsAt: string | null;
  /** is_festival or multi_day: decides the tail Find keeps running past ends_at (see isFindOver). */
  eventIsFestival: boolean;
  /** The festival family's parent event id (festival_parent_event_id), or this event's own id. */
  eventRootId: string | null;
  eventVenue: string | null;
  /** The venue's point (venues.latitude/longitude): the native publisher's on-site test (2026-10-12). */
  eventVenueLat: number | null;
  eventVenueLng: number | null;
  /** groups.qa_bypass_presence: visible anywhere, so the native off-site rule must not apply. */
  qaBypassPresence: boolean;
  /** The event's card image (hero as fallback): the group's picture in pickers and lists. */
  eventImageUrl: string | null;
  /**
   * Over for Find and the Groups tab: the event's last night has ended AND the tail people need afterwards has
   * run (see isFindOver), or it was cancelled (files under Past).
   */
  isPast: boolean;
  isCancelled: boolean;
};

interface GroupMemberRow {
  group_id: string;
  groups:
    | {
        id: string;
        name: string;
        event_id: string | null;
      }
    | {
        id: string;
        name: string;
        event_id: string | null;
      }[]
    | null;
}

interface EventRow {
  id: string;
  name: string | null;
  date: string | null;
  /** getEventStartMs of the row: date-only events start at midnight where they are, not UTC midnight. */
  startMs: number;
  venue: string | null;
  venueLat: number | null;
  venueLng: number | null;
  imageUrl: string | null;
  cancelled: boolean;
  /** Server-computed end: 6 AM venue-local after the last day (migration 134). */
  endsAt: string | null;
  /** multi_day: with no endsAt, assume three days (the server's rule without lineup days). */
  multiDay: boolean;
  /** is_festival: with multiDay, picks the longer tail after the event (isFindOver). */
  isFestival: boolean;
  /** festival_parent_event_id when this is a day child of a festival. */
  parentId: string | null;
}

function normalizeJoinedGroup(
  groups: GroupMemberRow['groups'],
): { id: string; name: string; event_id: string | null } | null {
  if (!groups) {
    return null;
  }
  if (Array.isArray(groups)) {
    return groups[0] ?? null;
  }
  return groups;
}

function pickString(row: Record<string, unknown>, keys: string[]): string | null {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === 'string' && value.trim().length > 0) {
      return value.trim();
    }
  }
  return null;
}

function normalizeJoinedVenue(venues: unknown): string | null {
  if (!venues) {
    return null;
  }
  const row = Array.isArray(venues) ? venues[0] : venues;
  if (!row || typeof row !== 'object') {
    return null;
  }
  const name = (row as { name?: unknown }).name;
  return typeof name === 'string' && name.trim().length > 0 ? name.trim() : null;
}

function joinedVenuePoint(venues: unknown): { venueLat: number | null; venueLng: number | null } {
  const row = (Array.isArray(venues) ? venues[0] : venues) as {
    latitude?: unknown;
    longitude?: unknown;
  } | null;
  const lat = Number(row?.latitude),
    lng = Number(row?.longitude);
  return Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    row?.latitude != null &&
    row?.longitude != null
    ? { venueLat: lat, venueLng: lng }
    : { venueLat: null, venueLng: null };
}

function parseEventRow(row: Record<string, unknown>): EventRow | null {
  const rawId = row.event_id ?? row.id;
  if (typeof rawId !== 'string' && typeof rawId !== 'number') {
    return null;
  }
  return {
    id: String(rawId),
    name: resolveEventDisplayName(row as Record<string, unknown>),
    date: pickString(row, ['event_datetime', 'event_date', 'date', 'starts_at']),
    startMs: getEventStartMs(row),
    venue: normalizeJoinedVenue(row.venues),
    ...joinedVenuePoint(row.venues),
    imageUrl: pickString(row, ['card_image_url', 'hero_image_url']),
    cancelled: ['cancelled', 'canceled'].includes(String(row.status ?? '').toLowerCase()),
    endsAt: pickString(row, ['ends_at']),
    multiDay: row.multi_day === true,
    isFestival: row.is_festival === true,
    parentId: pickString(row, ['festival_parent_event_id']),
  };
}

/** Sets run all night, so a show's night ends the next morning, not at its start time. */
const NIGHT_ENDS_HOUR = 6;
/** multi_day with no known last day runs three days - mirrors event_ends_at() (134; 153 adds a pass's named length, server only). */
const MULTI_DAY_ASSUMED_EXTRA_DAYS = 2;

/**
 * Whether the event is over. It used to be "the start time has passed", which filed a group under Past - and
 * dropped it from Find's upcoming groups - the moment doors opened: CRSSD at noon on its first day (2026-09-26),
 * so a friend opening Find there got no group and location never started. Now it is over at 6 AM the morning
 * after its LAST day: the server's events.ends_at when the row has it, else the same rule from the date.
 */
export function isEventOver(
  event: { date: string | null; multiDay: boolean; endsAt?: string | null },
  now: number,
): boolean {
  if (event.endsAt) {
    const ends = new Date(event.endsAt).getTime();
    if (Number.isFinite(ends)) {
      return now >= ends;
    }
  }
  if (!event.date) {
    return false;
  }
  // A bare date ("2026-09-26") parses as UTC midnight - the evening before on the west coast - so read it as local.
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(event.date);
  const start = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3])).getTime()
    : new Date(event.date).getTime();
  if (!Number.isFinite(start)) {
    return false;
  }
  const nightEnds = new Date(start);
  nightEnds.setDate(nightEnds.getDate() + 1 + (event.multiDay ? MULTI_DAY_ASSUMED_EXTRA_DAYS : 0));
  nightEnds.setHours(NIGHT_ENDS_HOUR, 0, 0, 0);
  return now > nightEnds.getTime();
}

/**
 * Hours Find keeps running past the event's end (Sam, 2026-10-04): after the last set people find their friends,
 * the exit, the pickup, the car; at a camping festival they are there until they pack up the next day. 6 h for a
 * show (noon the day after), 12 h for a festival / multi-day event (6 PM the day after its last day). Mirrors
 * FIND_TAIL_HOURS in src/utils/eventCutoff.js and event_presence_tail_hours() on the server (migration 140).
 */
export const FIND_TAIL_HOURS = { show: 3, festival: 3 } as const; // migration 147 (was 6 / 12)

/** Over for Find: the event is over (isEventOver) and its tail has run too. */
export function isFindOver(
  event: { date: string | null; multiDay: boolean; endsAt?: string | null; isFestival?: boolean },
  now: number,
): boolean {
  const hours =
    event.isFestival || event.multiDay ? FIND_TAIL_HOURS.festival : FIND_TAIL_HOURS.show;
  return isEventOver(event, now - hours * 60 * 60 * 1000);
}

function formatEventDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return '';
  }
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function mapRowToOption(
  row: GroupMemberRow,
  eventById: Map<string, EventRow>,
  now: number,
): UserGroupOption | null {
  const group = normalizeJoinedGroup(row.groups);
  if (!group?.id) {
    return null;
  }
  const event = group.event_id ? eventById.get(group.event_id) : undefined;
  const eventDate = event?.date ?? null;
  const isCancelled = Boolean(event?.cancelled);
  const isPast = isCancelled || (event ? isFindOver(event, now) : false);
  return {
    id: group.id,
    groupName: group.name ?? 'Group',
    eventId: group.event_id ?? null,
    eventName: event?.name ?? null,
    eventDate,
    eventStartsAt:
      event && Number.isFinite(event.startMs) ? new Date(event.startMs).toISOString() : null,
    eventEndsAt: event?.endsAt ?? null,
    eventIsFestival: Boolean(event?.isFestival || event?.multiDay),
    eventRootId: event ? (event.parentId ?? event.id) : null,
    eventVenue: event?.venue ?? null,
    eventVenueLat: event?.venueLat ?? null,
    eventVenueLng: event?.venueLng ?? null,
    qaBypassPresence: (group as { qa_bypass_presence?: boolean }).qa_bypass_presence === true,
    eventImageUrl: event?.imageUrl ?? null,
    isPast,
    isCancelled,
  };
}

function startOrInfinity(g: UserGroupOption): number {
  const ms = getEventStartMs(g as unknown as Record<string, unknown>);
  return Number.isFinite(ms) ? ms : Number.POSITIVE_INFINITY;
}

/**
 * Upcoming groups soonest first, then past ones most recent first. Which live group runs Find is not "the first" any more - see
 * pickCurrentLiveEvent (2026-10-16 audit: the earliest of two live events won all night).
 */
function sortGroups(groups: UserGroupOption[]): UserGroupOption[] {
  return [...groups].sort((a, b) => {
    if (a.isPast !== b.isPast) {
      return a.isPast ? 1 : -1;
    }
    const aMs = startOrInfinity(a);
    const bMs = startOrInfinity(b);
    if (!a.isPast) {
      if (aMs !== bMs) {
        return aMs - bMs;
      }
      return a.id.localeCompare(b.id);
    }
    if (aMs !== bMs) {
      return bMs - aMs;
    }
    return a.id.localeCompare(b.id);
  });
}

async function fetchEventDetails(
  client: SupabaseClient,
  eventIds: string[],
): Promise<Map<string, EventRow>> {
  const eventById = new Map<string, EventRow>();
  if (eventIds.length === 0) {
    return eventById;
  }

  // Verified against Prii schema: events.venue_id → venues.id; venue name from venues(name).
  const { data, error } = await client
    .from('events')
    .select(
      'event_id, event_name, display_name, display_name_override, event_date, event_time, event_datetime, timezone, ends_at, status, multi_day, is_festival, festival_parent_event_id, card_image_url, hero_image_url, venues(name, latitude, longitude, timezone)',
    )
    .in('event_id', eventIds);

  if (error) {
    return eventById;
  }

  for (const row of data ?? []) {
    const parsed = parseEventRow(row as Record<string, unknown>);
    if (parsed) {
      eventById.set(parsed.id, parsed);
    }
  }
  return eventById;
}

export async function fetchUserGroups(
  client: SupabaseClient,
  userId: string,
): Promise<UserGroupOption[]> {
  if (!userId) {
    return [];
  }

  const { data, error } = await client
    .from('group_members')
    .select(
      `
      group_id,
      groups (
        id,
        name,
        event_id,
        qa_bypass_presence
      )
    `,
    )
    .eq('user_id', userId)
    .is('left_at', null)
    .eq('removed', false);

  if (error) {
    throw new Error(error.message || 'Failed to load group memberships');
  }

  const rows = (data ?? []) as GroupMemberRow[];
  const eventIds = [
    ...new Set(
      rows
        .map((row) => normalizeJoinedGroup(row.groups)?.event_id)
        .filter((id): id is string => typeof id === 'string' && id.length > 0),
    ),
  ];

  const eventById = await fetchEventDetails(client, eventIds);
  const now = Date.now();
  const options = rows
    .map((row) => mapRowToOption(row, eventById, now))
    .filter((g): g is UserGroupOption => g !== null);

  return sortGroups(options);
}

/**
 * The group name leads — Find is scoped to a group the user named, and showing only
 * the event name made the "Group" card read as if the group had vanished.
 * Generic auto-names ("Group 1") carry no meaning, so those fall back to the event.
 */
export function groupPrimaryLabel(group: UserGroupOption): string {
  const groupName = group.groupName.trim();
  if (groupName && !isGenericGroupName(groupName)) {
    return groupName;
  }
  const event = group.eventName?.trim();
  if (event) {
    return event;
  }
  return groupName || 'Unnamed group';
}

export function isGenericGroupName(name: string): boolean {
  return /^group\s*\d+$/i.test(name.trim());
}

export function groupModalPrimaryLabel(group: UserGroupOption): string {
  const event = group.eventName?.trim();
  if (event) {
    const datePart = group.eventDate ? formatEventDate(group.eventDate) : '';
    return datePart ? `${event} · ${datePart}` : event;
  }
  const groupName = group.groupName.trim();
  if (groupName && !isGenericGroupName(groupName)) {
    return groupName;
  }
  return groupName || 'Unnamed group';
}

export function groupModalSecondaryLabel(group: UserGroupOption): string | null {
  const groupName = group.groupName.trim();
  const event = group.eventName?.trim();
  const parts: string[] = [];

  if (event) {
    if (groupName) {
      parts.push(groupName);
    }
    if (group.isPast) {
      parts.push(group.isCancelled ? 'Cancelled' : 'Past');
    }
    return parts.length > 0 ? parts.join(' · ') : null;
  }

  if (groupName && isGenericGroupName(groupName)) {
    parts.push(groupName);
  }
  if (group.eventDate) {
    const formatted = formatEventDate(group.eventDate);
    if (formatted) {
      parts.push(formatted);
    }
  }
  if (group.isPast) {
    parts.push(group.isCancelled ? 'Cancelled' : 'Past');
  }
  if (parts.length === 0 && groupName) {
    return groupName;
  }
  return parts.length > 0 ? parts.join(' · ') : null;
}

export function groupSecondaryLabel(group: UserGroupOption): string | null {
  const parts: string[] = [];
  const event = group.eventName?.trim();
  if (event && groupPrimaryLabel(group) !== event) {
    parts.push(event);
  }
  if (group.eventDate) {
    const formatted = formatEventDate(group.eventDate);
    if (formatted) {
      parts.push(formatted);
    }
  }
  if (group.eventVenue?.trim()) {
    parts.push(group.eventVenue.trim());
  }
  if (group.isPast) {
    parts.push(group.isCancelled ? 'Cancelled' : 'Past');
  }
  return parts.length > 0 ? parts.join(' · ') : null;
}
